// The one Socket.IO connection for the whole app — reusable the same way
// axiosInstance is: a module imports `getSocket()`/`onDeliveryEvent()` rather
// than creating its own connection. Delivery is the first caller; any later
// module (tasks, cheques, …) listens on this same socket instead of opening a
// second one.
import { io } from 'socket.io-client';

// The backend socket lives on the SAME origin/port as the REST API, not on
// REACT_APP_API_URL's default of '' (same-origin as the frontend) — Socket.IO
// needs an explicit URL, axios does not, because axios can rely on the
// browser resolving a relative baseURL but socket.io-client cannot.
const SOCKET_URL = process.env.REACT_APP_API_URL || window.location.origin;

let socket = null;

// ---------------------------------------------------------------------------
// Shared recovery helpers. Nothing here makes Socket.IO itself persistent —
// no replay, no ack, no server-side queue. Each registered callback is just
// told "the socket is connected again" or "the tab is visible again", and is
// expected to do what FrozenPickPortal.js's BoardTab already does inline:
// re-run its OWN existing fetch, because the database is the source of
// truth and a missed live event must only ever mean a delayed discovery, not
// a lost one. Centralised here so every hook/component reuses ONE mechanism
// instead of five copies of the same retry-attach/cleanup logic.
// ---------------------------------------------------------------------------

const reconnectCallbacks = new Set();

function notifyReconnectCallbacks() {
    reconnectCallbacks.forEach((cb) => {
        try {
            cb();
        } catch (err) {
            console.error('[socketClient] reconnect callback failed:', err);
        }
    });
}

/**
 * Registers `callback` to run every time the socket (re)connects — including
 * the very first connect, same semantics FrozenPickPortal.js's `socket.on
 * ('connect', load)` already relies on. A harmless extra fetch alongside a
 * component's own mount-fetch is the accepted tradeoff for one shared,
 * simple mechanism. Returns an unsubscribe function — ALWAYS call it from
 * the caller's effect cleanup, or callbacks accumulate across remounts.
 */
export function onSocketReconnect(callback) {
    reconnectCallbacks.add(callback);
    return () => reconnectCallbacks.delete(callback);
}

/**
 * Listens for `event` for as long as the caller stays subscribed — across
 * socket REPLACEMENTS, not just reconnects. That distinction is the point:
 * connectSocket() throws the socket away and builds a new one whenever the
 * token rotates (the 12-minute silent refresh), and a listener attached
 * directly to the old object goes deaf without any error. That is exactly how
 * the frozen pick board stopped updating live while the toast (which happened
 * to re-attach on a user change) kept working. Registered handlers are kept
 * here and attached to every socket connectSocket() creates; a subscription
 * made before any socket exists simply waits for the first one. Returns an
 * unsubscribe — ALWAYS call it from the effect cleanup.
 */
const persistentListeners = new Set();

export function onSocketEvent(event, handler) {
    const entry = { event, handler };
    persistentListeners.add(entry);
    if (socket) socket.on(event, handler);
    return () => {
        persistentListeners.delete(entry);
        if (socket) socket.off(event, handler);
    };
}

// The other half of "was this stale while I wasn't looking": a background
// tab that stayed socket-connected the whole time (so `onSocketReconnect`
// never fires) but simply wasn't rendering. One shared `visibilitychange`
// listener for the whole app, attached lazily on first use.
const visibleCallbacks = new Set();
let visibilityListenerAttached = false;

function ensureVisibilityListener() {
    if (visibilityListenerAttached || typeof document === 'undefined') return;
    visibilityListenerAttached = true;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        visibleCallbacks.forEach((cb) => {
            try {
                cb();
            } catch (err) {
                console.error('[socketClient] visibility callback failed:', err);
            }
        });
    });
}

/** Registers `callback` to run whenever the tab becomes visible again. */
export function onAppVisible(callback) {
    ensureVisibilityListener();
    visibleCallbacks.add(callback);
    return () => visibleCallbacks.delete(callback);
}

/**
 * Opens (or reuses) the socket for the given JWT. Called from AppShell,
 * which is mounted for the lifetime of an authenticated session — so this is
 * naturally re-invoked with a fresh token whenever axiosInstance's silent
 * refresh rotates it, keeping the connection authenticated without the caller
 * having to think about expiry.
 */
export function connectSocket(token) {
    if (!token) return null;

    // Reused whenever the token matches — even mid-handshake (not yet
    // `.connected`). AppShell mounts useDeliveryRealtime, useAnnouncements
    // and useAdminBubbles in the same render pass, and each independently
    // calls connectSocket(token). Requiring `.connected` here used to mean
    // the second and third callers would see a still-connecting socket,
    // disconnect it, and start a brand new one — each hook cancelling the
    // previous one's handshake in turn, so the socket never actually
    // finished connecting ("WebSocket is closed before the connection is
    // established", repeated once per hook). Only a genuine token change
    // (silent refresh rotating it) should tear down and reconnect.
    if (socket && socket.auth?.token === token) {
        return socket;
    }

    if (socket) {
        socket.disconnect();
    }

    socket = io(SOCKET_URL, {
        auth: { token },
        // Both transports, but a failed websocket falls back to polling rather
        // than the connection just dying — some corporate/hotel networks block
        // raw WebSocket upgrades outright.
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000
    });

    // Fires on the initial connect AND every reconnect — see
    // onSocketReconnect's header comment for why that's the desired shape.
    socket.on('connect', notifyReconnectCallbacks);
    // Carry every onSocketEvent subscription over to the new socket.
    persistentListeners.forEach(({ event, handler }) => socket.on(event, handler));

    return socket;
}

export function disconnectSocket() {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
}

export function getSocket() {
    return socket;
}
